import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import LoginUserType from "../classes/LoginUserType";
import Loader from "./Loader";
import ActivityTracker from "../models/ActivityTracker";
import LabelWithInputField from "./LabelWithInputField";
import PageNavigator from "../classes/PageNavigator";
import UserType from "../classes/UserType";
import Result from "../classes/Result";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import Button from "./Button";
import LoginResult from "../classes/LoginResult";
import Administrator from "../models/Administrator";
import DateTime from "../core/DateTime";
import TextView from "./TextView";
import ActivityType from "../classes/ActivityType";
import BaseUser from "../models/BaseUser";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _ForgotPasswordButtonOnPressed = (d3eState: LoginScreenRefs) => void;

type _SignInButtonOnPressed = (d3eState: LoginScreenRefs) => void;

type _EmailFieldOnChanged = (text: string, d3eState: LoginScreenRefs) => void;

type _PasswordFieldOnChanged = (
  text: string,
  d3eState: LoginScreenRefs
) => void;

export interface LoginScreenProps extends BaseUIProps {
  key?: string;
}
/// To store state data for LoginScreen
class LoginScreenRefs {
  public forgotPasswordButton: ForgotPasswordButtonState =
    new ForgotPasswordButtonState();
  public signInButton: SignInButtonState = new SignInButtonState();
}

interface SignInButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: LoginScreenRefs;
  _signInButtonHandler?: _SignInButtonOnPressed;
}

class SignInButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _SignInButtonWithState extends ObservableComponent<SignInButtonWithStateProps> {
  signInButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SignInButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get signInButton(): SignInButtonState {
    return this.props.d3eState.signInButton;
  }
  public get d3eState(): LoginScreenRefs {
    return this.props.d3eState;
  }
  public get _signInButtonHandler(): _SignInButtonOnPressed {
    return this.props._signInButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("signInButton", null, this.signInButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["signInButton", "signInButton.", "signInButton.hover"],
      this.rebuild
    );
  }
  public signInButtonOnEnter(event): void {
    return this.signInButton.setHover(true);
  }
  public signInButtonOnExit(event): void {
    return this.signInButton.setHover(false);
  }
  public dispose(): void {
    this.signInButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      expand: true,
      margin: ui.EdgeInsets.zero,
      child: Button({
        padding: this.signInButton.hover
          ? ui.EdgeInsets.all(10.0, new Map())
          : ui.EdgeInsets.all(10.0, new Map()),
        decoration: this.signInButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.signInButton.disable,
        onPressed: () => {
          this._signInButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Log In", className: "hc" }),
        onEnter: (event) => {
          this.signInButtonOnEnter(event);
        },
        onExit: (event) => {
          this.signInButtonOnExit(event);
        },
      }),
      className: "x023 hc h",
    });
  }
}
function SignInButtonWithState(props: SignInButtonWithStateProps) {
  return React.createElement(_SignInButtonWithState, props);
}

interface ForgotPasswordButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: LoginScreenRefs;
  _forgotPasswordHandler?: _ForgotPasswordButtonOnPressed;
}

class ForgotPasswordButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _ForgotPasswordButtonWithState extends ObservableComponent<ForgotPasswordButtonWithStateProps> {
  forgotPasswordButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ForgotPasswordButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get forgotPasswordButton(): ForgotPasswordButtonState {
    return this.props.d3eState.forgotPasswordButton;
  }
  public get d3eState(): LoginScreenRefs {
    return this.props.d3eState;
  }
  public get _forgotPasswordHandler(): _ForgotPasswordButtonOnPressed {
    return this.props._forgotPasswordHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "forgotPasswordButton",
      null,
      this.forgotPasswordButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "forgotPasswordButton",
        "forgotPasswordButton.",
        "forgotPasswordButton.hover",
      ],
      this.rebuild
    );
  }
  public forgotPasswordButtonOnEnter(event): void {
    return this.forgotPasswordButton.setHover(true);
  }
  public forgotPasswordButtonOnExit(event): void {
    return this.forgotPasswordButton.setHover(false);
  }
  public dispose(): void {
    this.forgotPasswordButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      disable: this.forgotPasswordButton.disable,
      onPressed: () => {
        this._forgotPasswordHandler(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({
        data: "Forgot Password",
        style: new ui.TextStyle({
          fontFamily: "Open Sans",
          fontSize: 16,
          color: cStyle.c19,
        }),
        className: "x0b4",
      }),
      onEnter: (event) => {
        this.forgotPasswordButtonOnEnter(event);
      },
      onExit: (event) => {
        this.forgotPasswordButtonOnExit(event);
      },
      className: "xbde",
    });
  }
}
function ForgotPasswordButtonWithState(
  props: ForgotPasswordButtonWithStateProps
) {
  return React.createElement(_ForgotPasswordButtonWithState, props);
}

class _LoginScreenState extends ObservableComponent<LoginScreenProps> {
  d3eState: LoginScreenRefs = new LoginScreenRefs();
  email: string = "";
  password: string = "";
  emailInfo: boolean = false;
  passwordInfo: boolean = false;
  errorMessages: string = "";
  showError: boolean = false;
  showUserError: boolean = false;
  loaded: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: LoginScreenProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "email",
        "emailInfo",
        "loaded",
        "password",
        "passwordInfo",
        "showError",
        "showUserError",
      ],
      this.rebuild
    );
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this.email !== val;

    if (!isValChanged) {
      return;
    }

    this.email = val;

    this.fire("email", this);
  }
  public setPassword(val: string): void {
    let isValChanged: boolean = this.password !== val;

    if (!isValChanged) {
      return;
    }

    this.password = val;

    this.fire("password", this);
  }
  public setEmailInfo(val: boolean): void {
    let isValChanged: boolean = this.emailInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.emailInfo = val;

    this.fire("emailInfo", this);
  }
  public setPasswordInfo(val: boolean): void {
    let isValChanged: boolean = this.passwordInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.passwordInfo = val;

    this.fire("passwordInfo", this);
  }
  public setErrorMessages(val: string): void {
    let isValChanged: boolean = this.errorMessages !== val;

    if (!isValChanged) {
      return;
    }

    this.errorMessages = val;

    this.fire("errorMessages", this);
  }
  public setShowError(val: boolean): void {
    let isValChanged: boolean = this.showError !== val;

    if (!isValChanged) {
      return;
    }

    this.showError = val;

    this.fire("showError", this);
  }
  public setShowUserError(val: boolean): void {
    let isValChanged: boolean = this.showUserError !== val;

    if (!isValChanged) {
      return;
    }

    this.showUserError = val;

    this.fire("showUserError", this);
  }
  public setLoaded(val: boolean): void {
    let isValChanged: boolean = this.loaded !== val;

    if (!isValChanged) {
      return;
    }

    this.loaded = val;

    this.fire("loaded", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return this.loaded
      ? ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.center,
              children: [
                ui.Column({
                  mainAxisAlignment: ui.MainAxisAlignment.center,
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        ui.AssetImage({
                          path: "images/nok logo 1.png",
                          width: 230,
                          height: 74,
                          fit: ui.BoxFit.cover,
                          className: "x04e hc vc",
                          key: "0",
                        }),
                      ],
                      className: "x2bd hc h",
                      key: "0",
                    }),
                    ui.Container({
                      decoration: new ui.BoxDecoration({ color: cStyle.c21 }),
                      child: LabelWithInputField({
                        name: "Email",
                        placeHolder: "Enter Email Address",
                        value: this.email,
                        errors: this.emailInfo
                          ? ["Please Enter Email Address"]
                          : [],
                        isRequired: true,
                        onTap: () => {
                          this.onTapEmailFiledHandler(this.d3eState);
                        },
                        onChanged: (text) => {
                          this.emailFieldonChanged(text, this.d3eState);
                        },
                      }),
                      key: "1",
                      className: "x74f hc h",
                    }),
                    ui.Container({
                      decoration: new ui.BoxDecoration({ color: cStyle.c21 }),
                      margin: ui.EdgeInsets.fromLTRB(
                        0.0,
                        10.0,
                        0.0,
                        0.0,
                        new Map()
                      ),
                      child: LabelWithInputField({
                        name: "Password",
                        placeHolder: "Enter Password",
                        value: this.password,
                        obscureText: true,
                        errors: this.passwordInfo
                          ? ["Please Enter Password"]
                          : [],
                        isRequired: true,
                        onTap: () => {
                          this.onTapPasswordFiledHandler(this.d3eState);
                        },
                        onSubmitted: (value) => {
                          this.onSubmittedPasswordField(value, this.d3eState);
                        },
                        onChanged: (text) => {
                          this.passwordFieldonChanged(text, this.d3eState);
                        },
                      }),
                      key: "2",
                      className: "x825 hc h",
                    }),
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.end,
                      children: [
                        ForgotPasswordButtonWithState({
                          d3eState: this.d3eState,
                          _forgotPasswordHandler: this.forgotPasswordHandler,
                          key: "0",
                        }),
                      ],
                      className: "xb48 hc h",
                      key: "3",
                    }),
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        SignInButtonWithState({
                          d3eState: this.d3eState,
                          _signInButtonHandler: this.signInButtonHandler,
                          key: "0",
                        }),
                      ],
                      className: "xaac hc h",
                      key: "4",
                    }),
                  ],
                  className: "x660 hc",
                  key: "0",
                }),
                this.showError
                  ? TextView({
                      data: "Invalid authentication details. Please Enter valid details.",
                      style: new ui.TextStyle({
                        color: cStyle.c8,
                        fontFamily: "Open Sans",
                        fontSize: 14,
                      }),
                      className: "x06e hc",
                    })
                  : [],
                this.showUserError
                  ? TextView({
                      data: "Your account is not active. Please contact your administrator.",
                      style: new ui.TextStyle({
                        color: cStyle.c8,
                        fontFamily: "Open Sans",
                        fontSize: 14,
                      }),
                      className: "xc3d hc",
                    })
                  : [],
              ],
              className: "xfa0 hc vc",
              key: "0",
            }),
          ],
          className: ui.join(this.props.className, "LoginScreen xd7a hc vc"),
          ...copyBaseUIProps(this.props),
        })
      : ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.center,
              children: [
                Loader({ key: "0" }),
                TextView({
                  data: "Loading App......",
                  className: "x507",
                  key: "1",
                }),
              ],
              className: "x08b hc h",
              key: "0",
            }),
          ],
          className: ui.join(this.props.className, "LoginScreen hc vc"),
          ...copyBaseUIProps(this.props),
        });
  }
  public forgotPasswordHandler = (d3eState: LoginScreenRefs): void => {
    this.navigator.pushForgotPasswordPage();
  };
  public onSubmittedPasswordField = (
    value: string,
    d3eState: LoginScreenRefs
  ): void => {
    this.authenticatingUser();
  };
  public authenticatingUser = async (): Promise<void> => {
    if (this.email.isEmpty) {
      this.setEmailInfo(true);
    }

    if (this.password.isEmpty) {
      this.setPasswordInfo(true);
    }

    if (this.emailInfo || this.passwordInfo) {
      return;
    }

    let result: LoginResult =
      await Query.get().loginAdministratorUserWithEmailAndPassword(
        UsageConstants.QUERY_LOGINADMINISTRATORUSERWITHEMAILANDPASSWORD_LOGINSCREEN_EVENTHANDLERS_AUTHENTICATINGUSER_BLOCK,
        { email: this.email, password: this.password }
      );

    if (result.success) {
      let activityTracker: ActivityTracker = new ActivityTracker({
        userType: LoginUserType.Administrator,
        administrator: result.userObject as Administrator,
        activityType: ActivityType.Login,
        createDate: DateTime.now(),
        description: "Admin Logged In",
      });

      let result2: Result<ActivityTracker> = await activityTracker.save();

      this.navigator.pushHomePage({
        admin: result.userObject as Administrator,
      });
    } else {
      let res: LoginResult =
        await Query.get().loginNOKUserUserWithEmailAndPassword(
          UsageConstants.QUERY_LOGINNOKUSERUSERWITHEMAILANDPASSWORD_LOGINSCREEN_EVENTHANDLERS_AUTHENTICATINGUSER_BLOCK,
          { email: this.email, password: this.password }
        );

      if (res.success) {
        if ((res.userObject as NOKUser).status) {
          let activityTracker1: ActivityTracker = new ActivityTracker({
            userType:
              (res.userObject as NOKUser).userType === UserType.Admin
                ? LoginUserType.Admin
                : LoginUserType.Staff,
            user: res.userObject as NOKUser,
            activityType: ActivityType.Login,
            createDate: DateTime.now(),
            description: "User Logged In",
          });

          let result2: Result<ActivityTracker> = await activityTracker1.save();

          this.navigator.pushHomePage({ user: res.userObject as NOKUser });

          this.setShowUserError(false);
        } else {
          this.setShowUserError(true);
        }
      } else {
        this.setErrorMessages(result.failureMessage);

        if (this.errorMessages === result.failureMessage) {
          this.setShowError(true);
        }
      }
    }
  };
  public onInit = async (): Promise<void> => {
    let baseUser: BaseUser = await Query.get().currentUser();

    if (baseUser instanceof Administrator) {
      this.navigator.pushHomePage({ admin: baseUser as Administrator });
    } else if (baseUser instanceof NOKUser) {
      this.navigator.pushHomePage({ user: baseUser as NOKUser });
    }

    this.setLoaded(true);
  };
  public signInButtonHandler = (d3eState: LoginScreenRefs): void => {
    this.authenticatingUser();
  };
  public onTapEmailFiledHandler = (d3eState: LoginScreenRefs): void => {
    this.setEmailInfo(false);
  };
  public onTapPasswordFiledHandler = (d3eState: LoginScreenRefs): void => {
    this.setPasswordInfo(false);
  };
  public emailFieldonChanged = (
    val: string,
    d3eState: LoginScreenRefs
  ): void => {
    this.setEmail(val);
  };
  public passwordFieldonChanged = (
    val: string,
    d3eState: LoginScreenRefs
  ): void => {
    this.setPassword(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get forgotPasswordButton() {
    return this.d3eState.forgotPasswordButton;
  }
  public get signInButton() {
    return this.d3eState.signInButton;
  }
}
export default function LoginScreen(props: LoginScreenProps) {
  return React.createElement(_LoginScreenState, props);
}
